import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import type { LunarReceiptDto } from '@lunary/api-client'
import { ApiError } from '../../shared/api/client'
import { formatNumber } from '../../shared/ui/charts/scale'
import { RECEIPT_STATUS_LABELS, formatDateTime, formatReceiptDate, shortId } from './labels'
import { RECEIPTS_PAGE, useCancelReceipt, useIssueReceipt, useReceipts } from './queries'
import { ReceiptIssueDialog } from './receipt-issue-dialog'
import styles from './lunars.module.css'

type View = 'todo' | 'done'

function badgeClass(r: LunarReceiptDto): string {
  if (r.overdue || r.status === 'cancel_todo') return 'badge danger'
  return r.status === 'issued' ? 'badge ok' : 'badge'
}

function errorText(error: Error | null): string | null {
  if (!error) return null
  return error instanceof ApiError ? error.message : 'Не получилось, попробуйте ещё раз'
}

function CopyButton({ text, label }: { text: string; label: string }) {
  const [done, setDone] = useState(false)
  return (
    <button
      type="button"
      className="secondary"
      onClick={() => {
        void navigator.clipboard.writeText(text).then(() => {
          setDone(true)
          window.setTimeout(() => setDone(false), 1500)
        })
      }}
    >
      {done ? 'Скопировано' : label}
    </button>
  )
}

export function ReceiptsTab() {
  const [view, setView] = useState<View>('todo')
  const [offset, setOffset] = useState(0)
  const [issuing, setIssuing] = useState<LunarReceiptDto | null>(null)
  const receipts = useReceipts(view, offset)
  const cancel = useCancelReceipt()
  const issue = useIssueReceipt()
  const data = receipts.data
  const pick = (v: View) => {
    setView(v)
    setOffset(0)
  }

  return (
    <div className={styles.stack}>
      <p className={styles.caption}>
        Чеки по оплатам картой Людмила оформляет в «Мой налог» вручную: скопируйте название и сумму, внесите продажу датой оплаты,
        затем вставьте сюда ссылку на чек. Ссылка появится у покупателя в истории лунаров. Возвращённые платежи нужно аннулировать в
        «Моём налоге» и отметить здесь.
      </p>
      <div className={styles.periods} role="radiogroup" aria-label="Чеки">
        <button role="radio" aria-checked={view === 'todo'} className={view === 'todo' ? styles.periodActive : styles.period} onClick={() => pick('todo')}>
          К оформлению {data && data.todo_count > 0 && <span className={data.overdue_count > 0 ? 'badge danger' : 'badge'}>{formatNumber(data.todo_count)}</span>}
        </button>
        <button role="radio" aria-checked={view === 'done'} className={view === 'done' ? styles.periodActive : styles.period} onClick={() => pick('done')}>
          Оформленные
        </button>
      </div>
      {cancel.isError && <p className="error-box">{errorText(cancel.error)}</p>}
      <div className="card-panel">
        <div className={styles.scroll}>
          <table className="users-table">
            <thead>
              <tr>
                <th>Оплата</th>
                <th>Для чека</th>
                <th>Статус</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data?.items.map((r) => (
                <tr key={r.id}>
                  <td>
                    {formatDateTime(r.paid_at)}
                    <div className={`${styles.mono} muted`}>
                      {shortId(r.id)}
                      {' · '}
                      <Link to="/users/$userId" params={{ userId: r.user_id }}>
                        {shortId(r.user_id)}
                      </Link>
                    </div>
                  </td>
                  <td>
                    <div>{r.service_name}</div>
                    <div className="muted">
                      {formatNumber(r.amount)} ₽ · {formatReceiptDate(r.paid_at)} · физлицо, без НДС
                    </div>
                  </td>
                  <td>
                    <span className={badgeClass(r)}>{r.overdue ? 'Дольше суток' : (RECEIPT_STATUS_LABELS[r.status] ?? r.status)}</span>
                    {r.receipt_url && (
                      <div>
                        <a href={r.receipt_url} target="_blank" rel="noopener noreferrer">
                          Открыть чек
                        </a>
                        <span className="muted"> · {formatDateTime(r.receipt_canceled_at ?? r.receipt_issued_at)}</span>
                      </div>
                    )}
                  </td>
                  <td>
                    <div className={styles.actions}>
                      {r.status === 'todo' && (
                        <>
                          <CopyButton text={r.service_name} label="Название" />
                          <CopyButton text={String(r.amount)} label="Сумма" />
                          <button className="secondary" onClick={() => setIssuing(r)}>
                            Вставить ссылку
                          </button>
                        </>
                      )}
                      {r.status === 'issued' && (
                        <button className="secondary" onClick={() => setIssuing(r)}>
                          Заменить ссылку
                        </button>
                      )}
                      {r.status === 'cancel_todo' && (
                        <button className="secondary" disabled={cancel.isPending} onClick={() => cancel.mutate(r.id)}>
                          Аннулирован в «Мой налог»
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {data?.items.length === 0 && (
                <tr>
                  <td colSpan={4} className="muted">
                    {view === 'todo' ? 'Все чеки оформлены.' : 'Оформленных чеков пока нет.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {receipts.isPending && <div className="spinner" aria-label="Загрузка" />}
        {receipts.isError && <p className="error-box">Не удалось загрузить чеки.</p>}
        {data && data.total > RECEIPTS_PAGE && (
          <div className={styles.pager}>
            <button className="secondary" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - RECEIPTS_PAGE))}>
              Назад
            </button>
            <button className="secondary" disabled={offset + RECEIPTS_PAGE >= data.total} onClick={() => setOffset(offset + RECEIPTS_PAGE)}>
              Дальше
            </button>
          </div>
        )}
      </div>
      <ReceiptIssueDialog
        receipt={issuing}
        pending={issue.isPending}
        error={errorText(issue.error)}
        onSubmit={(url) => {
          if (issuing) issue.mutate({ id: issuing.id, url }, { onSuccess: () => setIssuing(null) })
        }}
        onClose={() => {
          issue.reset()
          setIssuing(null)
        }}
      />
    </div>
  )
}
